import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    image: string;
    newsLink: string;
    newsTitle: string;
    date: Date;
    extendedStyles?: NewsTileStyles;
}

export interface NewsTileStyles {
    wrapper?: InjectableCss;
    image?: InjectableCss;
    content?: InjectableCss;
    dateLabel?: ClickableProps;
    projectTitle?: TypographyProps;
    productsContainer?: InjectableCss;
    container?: InjectableCss;
}

export const newsTileStyles: NewsTileStyles = {
    wrapper: {
        css: css`
            width: 100%;
            max-width: 360px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                ]);
            }}
        `,
    },
    image: {
        css: css`
            cursor: pointer;
        `,
    },
    content: {
        css: css`
            width: 100%;
            height: 164px;
            display: flex;
            flex-direction: column;
            padding: 20px;
            background-color: ${UCSColors.gray10};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 146px;
                        padding: 16px 15px;
                    `,
                    css`
                        height: 166px;
                        padding: 16px 15px;
                    `,
                ]);
            }}
            @media only screen and (min-width: 550px) and (max-width: 700px) {
                height: 176px !important;
            }
        `,
    },
    dateLabel: {
        css: css`
            font-size: 13px;
            color: ${UCSColors.gray04};
            font-weight: 500;
            letter-spacing: 0.02em;
            margin-bottom: 8px;
        `,
    },
    projectTitle: {
        size: 20,
        color: UCSColors.gray00,
        css: css`
            flex: 1;
            line-height: 24px;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 17px;
                    `,
                    css`
                        font-size: 18px;
                    `,
                ]);
            }}
        `,
    },
    productsContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: nowrap;
            overflow-x: scroll;
            gap: 4px;
            margin-top: auto;

            ::-webkit-scrollbar-track {
                background: ${UCSColors.gray10};
            }
            ::-webkit-scrollbar-thumb {
                background: ${UCSColors.gray08};
            }
            ::-webkit-scrollbar-thumb:hover {
                background: ${UCSColors.gray07};
            }
        `,
    },
    container: {
        css: css`
            flex: 1;
            width: 100%;
            padding-top: 74%; 
            position: relative;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-top: 28%;
                    `,
                    css`
                        padding-top: 47%; 
                    `,
                ]);
            }}
        `,
    },
};

const NewsTileCard: FC<OwnProps> = props => {
    const { image, newsLink, newsTitle, date, extendedStyles } = props;
    const [styles] = React.useState(() => mergeToNew(newsTileStyles, extendedStyles));

    const navigateToPage = () => {
        window.location.href = newsLink;
    };

    const getDateString = () => {
        const newDate = new Date(date);
        const day = (`0${newDate.getDate()}`).slice(-2);
        const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(newDate);
        const year = newDate.getFullYear();
        return `${month} ${+day < 10 ? day.slice(1, 2) : day}, ${year}`;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <FitImage image={image} imageClickHandler={navigateToPage} focalPoint="center" containerStyles={styles.container}/>
            <StyledWrapper {...styles.content}>
                <Clickable {...styles.dateLabel} href={newsLink}>
                    {getDateString()}
                </Clickable>
                <Clickable href={newsLink}>
                    <Typography {...styles.projectTitle}>{newsTitle}</Typography>
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default NewsTileCard;
